import store from '@/store/store';
import reduce from 'lodash/reduce';
import filter from 'lodash/filter';
import partition from 'lodash/partition';
import groupBy from 'lodash/groupBy';
import forEach from 'lodash/forEach';
import { TYPE } from '@/constants';

import Collection from './collection';
import { Unit } from './index';

import SVG from 'svg.js';
import { Section } from './sections/section';
import { Seat } from './seat';
import { GroupBy } from '../typeHelper';

export default class Selection extends Collection {
  get seats() {
    return this.groupedUnits[TYPE.SEAT];
  }

  get units() {
    return filter(
      this.items,
      (item): item is Exclude<Unit, Seat | Section> => item.type !== TYPE.SEAT && item.type !== TYPE.SECTION
    );
  }

  get sections() {
    return filter(this.items, (item): item is Section => item.type === TYPE.SECTION);
  }

  get partitionedUnits() {
    return partition(this.items, (item): item is Seat => item.type === TYPE.SEAT);
  }

  get groupedUnits() {
    return groupBy(this.items, 'type') as GroupBy<Unit, 'type'>;
  }

  add(item: Unit) {
    if (this.items.includes(item)) return;
    const success = item.select();
    if (typeof success === 'boolean' && !success) return;
    super.add(item);
  }

  addAll(items: Unit[]) {
    forEach(items, (item) => this.add(item));
  }

  remove(item: Unit) {
    item.deselect();
    super.remove(item);
  }

  removeAll(items: Unit[]) {
    forEach(items, (item) => this.remove(item));
  }

  toggle(item: Unit, flag: boolean) {
    flag === true ? this.add(item) : this.remove(item);
  }

  purge() {
    this.items.forEach((item) => item.deselect());
    this.items = [];
  }

  purgeAndSelectUnit(unit: Unit) {
    forEach(this.items, (item) => {
      if (unit === item) return;
      this.remove(item);
    });

    this.add(unit);
  }

  purgeAndSelectUnits(units: Unit[]) {
    forEach(this.items, (item) => {
      if (units.includes(item)) {
        // remove from selection so that units are in correct order
        // but keep the internal units state as selected to avoid extra work
        return super.remove(item);
      }
      this.remove(item);
    });

    forEach(units, (unit) => {
      this.add(unit);
    });
  }

  rbox(parent?: SVG.Doc) {
    if (this.length === 0) return;

    const getRbox = (item: Unit) => (store.state.seatmap.isResizing ? item.shape?.rbox(parent) : item.rbox(parent));

    return reduce(this.items, (acc, item) => acc.merge(getRbox(item)) as SVG.RBox, getRbox(this.first!));
  }
}
